// MIT License - Copyright (c) 2026 Doug McNall

import React from 'react';
import { createPortal } from 'react-dom';
import { CAPTAIN_RANKS, ProgressionSystem } from '../utils/game/ProgressionSystem';
import './RankDetailsModal.css';

/**
 * Rank Details Modal
 * Shows full rank ladder with player progress and next-rank requirements
 */
const RankDetailsModal = ({ isOpen, onClose, stats: rawStats }) => {
  if (!isOpen) return null;

  // ProgressionSystem.getRank uses 'credits', but HUD passes 'creditsEarned'
  const stats = { ...rawStats, credits: rawStats.creditsEarned };

  const currentRank = ProgressionSystem.getRank(stats);
  const nextRank = ProgressionSystem.getNextRank(currentRank);
  const requirements = ProgressionSystem.getRequirements(stats, currentRank);
  const rankUpRewards = nextRank ? ProgressionSystem.getRankUpRewards(nextRank) : null;

  return createPortal(
    <div className="rank-modal-overlay" onClick={onClose}>
      <div className="rank-modal" onClick={(e) => e.stopPropagation()}>
        <div className="rank-modal-header">
          <span className="rank-modal-icon">⭐</span>
          <h2>CAPTAIN'S LOG — RANK PROGRESSION</h2>
          <button className="rank-modal-close" onClick={onClose}>✕</button>
        </div>

        {/* Current stats */}
        <div className="rank-stats-row">
          <div className="rank-stat">
            <span className="rank-stat-label">Systems Visited</span>
            <span className="rank-stat-value">{stats.systemsVisited.toLocaleString()}</span>
          </div>
          <div className="rank-stat">
            <span className="rank-stat-label">Credits Earned</span>
            <span className="rank-stat-value">{stats.credits.toLocaleString()}¢</span>
          </div>
          <div className="rank-stat">
            <span className="rank-stat-label">Combat Wins</span>
            <span className="rank-stat-value">{stats.combatWins.toLocaleString()}</span>
          </div>
        </div>

        {/* Full rank ladder */}
        <div className="rank-ladder">
          {CAPTAIN_RANKS.map((rank) => {
            const isCurrent = rank.rank === currentRank.rank;
            const isAchieved =
              stats.systemsVisited >= rank.systems &&
              stats.credits >= rank.credits &&
              stats.combatWins >= rank.combatWins;

            return (
              <div
                key={rank.rank}
                className={`rank-ladder-row ${isCurrent ? 'current' : ''} ${isAchieved ? 'achieved' : ''}`}
              >
                <div className="rank-ladder-level">LVL {rank.level}</div>
                <div className="rank-ladder-name">
                  {isCurrent && <span className="rank-current-arrow">▶ </span>}
                  {rank.rank}
                </div>
                <div className="rank-ladder-desc">{rank.description}</div>
                <div className="rank-ladder-reqs">
                  <span className={stats.systemsVisited >= rank.systems ? 'req-met' : 'req-unmet'}>
                    {stats.systemsVisited >= rank.systems ? '✓' : '○'} {rank.systems} sys
                  </span>
                  <span className={stats.credits >= rank.credits ? 'req-met' : 'req-unmet'}>
                    {stats.credits >= rank.credits ? '✓' : '○'} {rank.credits.toLocaleString()}¢
                  </span>
                  <span className={stats.combatWins >= rank.combatWins ? 'req-met' : 'req-unmet'}>
                    {stats.combatWins >= rank.combatWins ? '✓' : '○'} {rank.combatWins} wins
                  </span>
                </div>
              </div>
            );
          })}
        </div>

        {/* Next rank requirements */}
        {nextRank && !requirements.complete ? (
          <div className="rank-next-section">
            <div className="rank-next-title">NEXT RANK: {nextRank.rank}</div>
            <div className="rank-next-reqs">
              {requirements.requirements.map((req, idx) => (
                <div key={idx} className={`rank-next-req ${req.complete ? 'complete' : ''}`}>
                  <span className="req-check">{req.complete ? '✓' : '○'}</span>
                  <span className="req-label">{req.name}:</span>
                  <span className="req-progress">
                    {req.current.toLocaleString()} / {req.required.toLocaleString()}
                  </span>
                </div>
              ))}
            </div>
            {rankUpRewards && (
              <div className="rank-rewards">
                <span className="rank-rewards-label">Rank-up rewards: </span>
                <span className="rank-rewards-credits">+{rankUpRewards.credits.toLocaleString()}¢</span>
                {rankUpRewards.unlocks.length > 0 && (
                  <span className="rank-rewards-unlocks">
                    {' · '}{rankUpRewards.unlocks.join(', ')}
                  </span>
                )}
              </div>
            )}
          </div>
        ) : (
          <div className="rank-max-achieved">
            🏆 Maximum Rank Achieved — Legendary Explorer
          </div>
        )}

        <div className="rank-modal-footer">
          <button className="rank-modal-btn" onClick={onClose}>CLOSE</button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default RankDetailsModal;
